import { MaterialTotal, OTHERS_MATERIAL_ID, topMaterials } from './materials';
import {
  MUTED_TEAL,
  NAVY,
  SLATE_BLUE,
  SOFT_PURPLE,
  STEEL_GRAY,
  TEAL,
} from './palette';

/**
 * Categorical palette shared by the three material cards, by rank in the
 * period: the leaders in Navy and Teal (Principal 1 and 2), then Slate Blue,
 * Soft Purple and Muted Teal. Amber, Coral and Sage are left to the states,
 * so a material can never read as one.
 */
export const MATERIAL_PALETTE = [
  NAVY,
  TEAL,
  SLATE_BLUE,
  SOFT_PURPLE,
  MUTED_TEAL,
];
/** Steel Gray. */
export const OTHERS_COLOR = STEEL_GRAY;

export interface LegendItem {
  materialId: string;
  name: string;
  color: string;
}

/**
 * Top 5 materials plus "Others", with a fixed color per position. Built once
 * from the full range so the colors stay put when a selection is made.
 */
export const buildLegend = (
  totals: MaterialTotal[],
  catalog: { materialId: string; name: string }[],
  othersLabel: string
): LegendItem[] =>
  topMaterials(totals, catalog, MATERIAL_PALETTE.length).map((m, i) =>
    m.materialId === OTHERS_MATERIAL_ID
      ? { materialId: m.materialId, name: othersLabel, color: OTHERS_COLOR }
      : { materialId: m.materialId, name: m.name, color: MATERIAL_PALETTE[i] }
  );

export interface MaterialPart {
  materialId: string;
  units: number;
}

export interface MaterialGroup {
  key: string;
  label: string;
  total: number;
  /** Non-zero parts, in legend order. */
  parts: MaterialPart[];
}

const bucketOf = (materialId: string, legend: LegendItem[]): string =>
  legend.some((l) => l.materialId === materialId)
    ? materialId
    : OTHERS_MATERIAL_ID;

/** Sums units per key and legend bucket; groups without units are dropped. */
export const groupUnits = (
  rows: { key: string; materialId: string; units: number }[],
  legend: LegendItem[],
  labelOf: (key: string) => string
): MaterialGroup[] => {
  const byKey = new Map<string, Map<string, number>>();
  rows.forEach((row) => {
    if (row.units <= 0) return;
    const bucket = bucketOf(row.materialId, legend);
    const parts = byKey.get(row.key) ?? new Map<string, number>();
    parts.set(bucket, (parts.get(bucket) ?? 0) + row.units);
    byKey.set(row.key, parts);
  });

  return [...byKey.entries()].map(([key, parts]) => {
    const ordered = legend
      .filter((l) => parts.has(l.materialId))
      .map((l) => ({
        materialId: l.materialId,
        units: parts.get(l.materialId) as number,
      }));

    return {
      key,
      label: labelOf(key),
      total: ordered.reduce((sum, p) => sum + p.units, 0),
      parts: ordered,
    };
  });
};

/** One row per legend item (zeros included) for the totals card. */
export const bucketTotals = (
  totals: MaterialTotal[],
  legend: LegendItem[]
): (LegendItem & { units: number })[] => {
  const sums = new Map<string, number>();
  totals.forEach((t) => {
    const bucket = bucketOf(t.materialId, legend);
    sums.set(bucket, (sums.get(bucket) ?? 0) + t.units);
  });

  return legend.map((l) => ({ ...l, units: sums.get(l.materialId) ?? 0 }));
};
